/**
 * Slug → human label lookups for the customer detail view. The API stores raw
 * enum slugs (industry `ecomerce`, source `gg_ads`, lead-type `warm-lead`,
 * country `vn`); these maps turn them into the create-form's display labels so
 * the Basic-Info grid reads cleanly. Falls back to `humanize()` for unknowns.
 */
import { INDUSTRY_OPTIONS, CUSTOMER_SOURCES, CUSTOMER_LEAD_TYPES, type Option } from '../new/constants';
import { humanize } from './status-tones';

/** Build a `slug → label` Map from an `Option[]`. */
function toMap(options: Option[]): Map<string, string> {
  return new Map(options.map((o) => [o.value, o.label]));
}

const INDUSTRY_MAP = toMap(INDUSTRY_OPTIONS);
const SOURCE_MAP = toMap(CUSTOMER_SOURCES);
const LEAD_TYPE_MAP = toMap(CUSTOMER_LEAD_TYPES);

/** Small `customer_type` / `customer_country` label map. */
const MISC_MAP = new Map<string, string>([
  ['company', 'Company'],
  ['individual', 'Individual'],
  ['vn', 'Vietnam'],
]);

/**
 * Look up `value` in `map`; fall back to `humanize()`. Empty/nullish → undefined
 * so `InfoGrid` renders its em dash.
 */
export function labelFrom(map: Map<string, string>, value?: string | null): string | undefined {
  if (!value) return undefined;
  return map.get(value) ?? humanize(value);
}

export const industryLabel = (v?: string | null) => labelFrom(INDUSTRY_MAP, v);
export const sourceLabel = (v?: string | null) => labelFrom(SOURCE_MAP, v);
export const leadTypeLabel = (v?: string | null) => labelFrom(LEAD_TYPE_MAP, v);
export const miscLabel = (v?: string | null) => labelFrom(MISC_MAP, v);
